import React, { useState, useEffect } from 'react';
import useEscapeKey from '../hooks/useEscapeKey';
import { calculatePensionContribution, employeeContributionBreakdown, formatCurrency, monthlyGrossForPension } from '../utils/pensionCalc';

export default function PensionModal({ employee, pension, onSave, onClose, pensionableAdditions = 0 }) {
  useEscapeKey(onClose);
  const [form, setForm] = useState({
    status: 'enrolled',
    earnings_basis: 'qualifying',
    employer_type: 'percentage',
    employer_value: 3,
    employee_type: 'percentage',
    employee_value: 5,
    scheme_type: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (pension) {
      setForm({
        // pension prop is the employee row with pension columns joined in and
        // aliased (pension_status, etc.) — do NOT fall back to pension.status,
        // that's the employee's row status ('active') and would break the
        // form.status === 'enrolled' conditional below.
        status: pension.pension_status || 'enrolled',
        earnings_basis: pension.earnings_basis || 'qualifying',
        employer_type: pension.employer_type || 'percentage',
        employer_value: pension.employer_value ?? 0,
        employee_type: pension.employee_type || 'percentage',
        employee_value: pension.employee_value ?? 0,
        scheme_type: pension.scheme_type || '',
      });
    }
  }, [pension]);

  // Pro-rata-aware: part-time staff use their pro-rata pay, not the FTE salary.
  // Pensionable additions (e.g. a car allowance flagged to count) top up the base so
  // the preview matches the run sheet and the rest of the app.
  const monthlyGross = monthlyGrossForPension(employee) + (parseFloat(pensionableAdditions) || 0);

  const employerAmount = calculatePensionContribution(
    form.employer_type, form.employer_value, monthlyGross, form.earnings_basis
  );
  const employeeGross = calculatePensionContribution(
    form.employee_type, form.employee_value, monthlyGross, form.earnings_basis
  );
  // Under Relief at Source only 80% comes off net pay; HMRC tops up the rest
  // into the pot. Other schemes deduct the full gross. `employeeAmount` is the
  // payslip figure; `employeeBreakdown.pot` is what lands in the pension.
  const employeeBreakdown = employeeContributionBreakdown(employeeGross, form.scheme_type);
  const employeeAmount = employeeBreakdown.fromPay;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setError('');
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.status === 'enrolled' && !form.scheme_type) {
      setError('Pick a scheme type — needed so the run sheet knows where the contribution sits (pre-tax vs post-tax).');
      return;
    }
    setSaving(true);
    try {
      await onSave(employee.id, { ...form, scheme_type: form.scheme_type || null });
      onClose();
    } catch (err) {
      setError('Failed to save pension: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Pension - {employee.first_name} {employee.last_name}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 rounded-full p-2 text-2xl leading-none" aria-label="Close">&times;</button>
          </div>

          {error && <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded text-sm text-red-700 dark:text-red-300">{error}</div>}

          <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded">
            <p className="text-sm text-gray-600 dark:text-gray-300">Monthly Gross: <strong>{formatCurrency(monthlyGross)}</strong></p>
            {(parseFloat(pensionableAdditions) || 0) > 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">incl. {formatCurrency(pensionableAdditions)} pensionable additions</p>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
              <select name="status" value={form.status} onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                <option value="enrolled">Enrolled</option>
                <option value="deferred">Deferred</option>
                <option value="opted-out">Opted Out</option>
                <option value="not-eligible">Not Eligible</option>
                <option value="exempt">Exempt</option>
              </select>
            </div>

            {form.status === 'enrolled' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Scheme Type *
                  </label>
                  <select name="scheme_type" value={form.scheme_type} onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                    <option value="">— select —</option>
                    <option value="salary_sacrifice">Salary Sacrifice</option>
                    <option value="net_pay">Net Pay Arrangement</option>
                    <option value="relief_at_source">Relief at Source</option>
                  </select>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Salary sacrifice / net pay = pre-tax deduction. Relief at source = post-tax. Ask your pension provider if unsure.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Earnings Basis</label>
                  <select name="earnings_basis" value={form.earnings_basis} onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                    <option value="qualifying">Qualifying Earnings</option>
                    <option value="full">Full Earnings</option>
                  </select>
                </div>

                <fieldset className="border border-gray-200 dark:border-gray-600 rounded-md p-4">
                  <legend className="text-sm font-medium text-gray-700 dark:text-gray-300 px-2">Employer Contribution</legend>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm text-gray-600 dark:text-gray-400">Type</label>
                      <select name="employer_type" value={form.employer_type} onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                        <option value="percentage">Percentage</option>
                        <option value="fixed-amount">Fixed Amount (GBP)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 dark:text-gray-400">
                        Value {form.employer_type === 'percentage' ? '(%)' : '(GBP)'}
                      </label>
                      <input type="number" name="employer_value" step="0.01" min="0" inputMode="decimal"
                        max={form.employer_type === 'percentage' ? '100' : undefined}
                        value={form.employer_value} onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                    </div>
                    <p className={`text-sm ${employerAmount < 0 ? 'text-red-600 dark:text-red-400' : 'text-brand-600 dark:text-brand-400'}`}>
                      {employerAmount < 0 ? 'Value cannot be negative' : `Calculated: ${formatCurrency(employerAmount)} / month`}
                    </p>
                  </div>
                </fieldset>

                <fieldset className="border border-gray-200 dark:border-gray-600 rounded-md p-4">
                  <legend className="text-sm font-medium text-gray-700 dark:text-gray-300 px-2">Employee Contribution</legend>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm text-gray-600 dark:text-gray-400">Type</label>
                      <select name="employee_type" value={form.employee_type} onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                        <option value="percentage">Percentage</option>
                        <option value="fixed-amount">Fixed Amount (GBP)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 dark:text-gray-400">
                        Value {form.employee_type === 'percentage' ? '(%)' : '(GBP)'}
                      </label>
                      <input type="number" name="employee_value" step="0.01" min="0" inputMode="decimal"
                        max={form.employee_type === 'percentage' ? '100' : undefined}
                        value={form.employee_value} onChange={handleChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                    </div>
                    <p className={`text-sm ${employeeAmount < 0 ? 'text-red-600 dark:text-red-400' : 'text-brand-600 dark:text-brand-400'}`}>
                      {employeeAmount < 0
                        ? 'Value cannot be negative'
                        : `${employeeBreakdown.reliefAtSource ? 'Deducted from net pay' : 'Calculated'}: ${formatCurrency(employeeBreakdown.fromPay)} / month`}
                    </p>
                    {employeeBreakdown.reliefAtSource && employeeAmount >= 0 && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        + {formatCurrency(employeeBreakdown.taxRelief)} basic-rate tax relief → {formatCurrency(employeeBreakdown.pot)} into pot
                      </p>
                    )}
                  </div>
                </fieldset>

                <div className="p-3 bg-brand-50 dark:bg-brand-900/30 rounded">
                  <p className="text-sm font-medium text-brand-800 dark:text-brand-300">
                    Total into pot: {formatCurrency(employerAmount + employeeBreakdown.pot)} / month
                  </p>
                </div>
              </>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <button type="button" onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600">
                Cancel
              </button>
              <button type="submit" disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-md hover:bg-brand-700 disabled:opacity-50">
                {saving ? 'Saving...' : 'Save Pension'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
